import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Container, Chip, CircularProgress } from '@mui/material';
import axios from 'axios';
import { buildTextSx, usedFontLink } from '../../utils/landingText';

const dark = '#0a0a0a';

// 기본 배경 (봇 랜딩에 hero_images 미설정 시 사용)
const defaultHeroImages = [
  { src: '/hero-1.webp', s640: '/hero-1-640.webp', s1024: '/hero-1-1024.webp' },
  { src: '/hero-2.webp', s640: '/hero-2-640.webp', s1024: '/hero-2-1024.webp' },
  { src: '/hero-3.webp', s640: '/hero-3-640.webp', s1024: '/hero-3-1024.webp' },
  { src: '/hero-4.webp', s640: '/hero-4-640.webp', s1024: '/hero-4-1024.webp' },
  { src: '/hero-5.webp', s640: '/hero-5-640.webp', s1024: '/hero-5-1024.webp' },
];

const shade = (hex, amt) => {
  // 간단한 명도 조절
  try {
    const n = parseInt(hex.slice(1), 16);
    let r = (n >> 16) + amt, g = ((n >> 8) & 0xff) + amt, b = (n & 0xff) + amt;
    r = Math.max(0, Math.min(255, r)); g = Math.max(0, Math.min(255, g)); b = Math.max(0, Math.min(255, b));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  } catch { return hex; }
};

// 프리미엄 효과 키프레임 (네온 깜빡임·수평선·별·바닥격자·버튼 스윕)
const FX_KEYFRAMES = `
@keyframes fxOrbK{0%,100%{opacity:.45;transform:scale(1)}50%{opacity:.82;transform:scale(1.08)}}
@keyframes fxTwinkle{0%,100%{opacity:.2}50%{opacity:1}}
@keyframes fxFloor{from{transform:translateY(0)}to{transform:translateY(35px)}}
@keyframes fxHorizonK{0%,100%{opacity:.78}50%{opacity:1}}
@keyframes fxFlicker{0%,100%{opacity:1}92%{opacity:1}93%{opacity:.45}94%{opacity:1}97%{opacity:.62}98%{opacity:1}}
@keyframes fxSweep{from{transform:translateX(-130%)}to{transform:translateX(130%)}}
.fxOrb{transform-box:fill-box;transform-origin:center;animation:fxOrbK 8s ease-in-out infinite}
.fxStar{transform-box:fill-box;animation:fxTwinkle 3.2s ease-in-out infinite}
.fxHlines{animation:fxFloor 6s linear infinite}
.fxHorizon{transform-box:fill-box;transform-origin:center;animation:fxHorizonK 5.5s ease-in-out infinite}
.fxFlick{animation:fxFlicker 4s infinite}
@media (prefers-reduced-motion: reduce){.fxOrb,.fxStar,.fxHlines,.fxHorizon,.fxFlick{animation:none}}
`;
const FX_STARS = [[150,90,1.4,'0s'],[320,160,1.1,'.6s'],[480,70,1.6,'1.2s'],[610,210,1,'.3s'],[760,110,1.3,'1.8s'],[880,180,1.1,'.9s'],[1010,80,1.5,'2.1s'],[1150,150,1.2,'.5s'],[1290,90,1.3,'1.5s'],[1330,260,1,'2.4s'],[220,270,1.1,'1.1s'],[540,310,1.2,'.2s'],[980,300,1,'1.7s'],[1180,340,1.3,'.8s']];
const FX_RADIAL = [-360,-180,0,180,360,540,720,900,1080,1260,1440,1620,1800];
const FX_HLINES = [665,700,735,770,805,840,875,910,945,980];

// alltime01 신스웨이브 배경 씬 (테마색 적용)
const BgScene = ({ c1, c2 }) => (
  <Box component="svg" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true"
    sx={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
    <defs>
      <radialGradient id="fxGlowC" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor={c1} stopOpacity="0.30" /><stop offset="1" stopColor={c1} stopOpacity="0" /></radialGradient>
      <radialGradient id="fxGlowM" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor={c2} stopOpacity="0.26" /><stop offset="1" stopColor={c2} stopOpacity="0" /></radialGradient>
      <linearGradient id="fxFloorFade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fff" stopOpacity="0" /><stop offset="0.35" stopColor="#fff" stopOpacity="0.5" /><stop offset="1" stopColor="#fff" stopOpacity="0.9" /></linearGradient>
      <mask id="fxFloorMask"><rect x="0" y="700" width="1440" height="200" fill="url(#fxFloorFade)" /></mask>
      <radialGradient id="fxSun" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor="#dffaff" stopOpacity="0.8" /><stop offset="0.4" stopColor={c1} stopOpacity="0.45" /><stop offset="1" stopColor={c1} stopOpacity="0" /></radialGradient>
      <linearGradient id="fxHglow" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor={c1} stopOpacity="0" /><stop offset="0.5" stopColor={c1} stopOpacity="1" /><stop offset="1" stopColor={c1} stopOpacity="0" /></linearGradient>
      <filter id="fxBloom" x="-10%" y="-600%" width="120%" height="1300%"><feGaussianBlur stdDeviation="7" /></filter>
      <radialGradient id="fxVig" cx="0.5" cy="0.4" r="0.75"><stop offset="0" stopColor="#05060d" stopOpacity="0" /><stop offset="0.7" stopColor="#05060d" stopOpacity="0.25" /><stop offset="1" stopColor="#05060d" stopOpacity="0.9" /></radialGradient>
    </defs>
    <ellipse className="fxOrb" cx="300" cy="150" rx="560" ry="400" fill="url(#fxGlowC)" style={{ animationDuration: '7s' }} />
    <ellipse className="fxOrb" cx="1140" cy="820" rx="560" ry="420" fill="url(#fxGlowM)" style={{ animationDuration: '11s' }} />
    <ellipse className="fxOrb" cx="1180" cy="200" rx="340" ry="280" fill="url(#fxGlowC)" opacity="0.5" style={{ animationDuration: '9s' }} />
    <g fill="#cfeeff">{FX_STARS.map((s, i) => (<circle key={i} className="fxStar" cx={s[0]} cy={s[1]} r={s[2]} style={{ animationDelay: s[3] }} />))}</g>
    <g mask="url(#fxFloorMask)">
      <g stroke={c1} strokeOpacity="0.18" strokeWidth="1">{FX_RADIAL.map((x, i) => (<line key={i} x1="720" y1="700" x2={x} y2="900" />))}</g>
      <g className="fxHlines" stroke={c1} strokeOpacity="0.16" strokeWidth="1.2">{FX_HLINES.map((y, i) => (<line key={i} x1="0" y1={y} x2="1440" y2={y} />))}</g>
    </g>
    <g className="fxHorizon">
      <ellipse cx="720" cy="700" rx="640" ry="150" fill="url(#fxSun)" opacity="0.5" />
      <rect x="0" y="690" width="1440" height="20" fill="url(#fxHglow)" opacity="0.45" filter="url(#fxBloom)" />
      <rect x="0" y="699" width="1440" height="2" fill="url(#fxHglow)" opacity="0.85" />
    </g>
    <rect width="1440" height="900" fill="url(#fxVig)" />
  </Box>
);

const DEFAULT_LANDING = {
  title: '', logo: '', primary: '#c9a96e', accent2: '#a8884d',
  subtitle: 'Premium Consultation',
  hero_images: [], overlay: 0.6, blur: 0,
  badge: '', headline: '', subheadline: '', button_text: '텔레그램 상담',
  promo: { enabled: false }, banners: [], countdown: { enabled: false },
  footer: '', footer_note: '', available_text: '24H AVAILABLE', side_text: '',
  fx: 'classic', benefits: [],
};

const useCountdown = (deadline) => {
  const [left, setLeft] = useState('');
  useEffect(() => {
    if (!deadline) return;
    const tick = () => {
      const diff = new Date(deadline).getTime() - Date.now();
      if (diff <= 0) { setLeft('마감되었습니다'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLeft(`${d}일 ${h}시간 ${m}분 ${s}초`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  return left;
};

// 익명 방문자 식별자: localStorage에 1회 생성·영구 보관 (고유 방문자 집계용, 개인정보 아님)
const getVisitorId = () => {
  try {
    let id = localStorage.getItem('tv_id');
    if (!id) {
      id = (window.crypto?.randomUUID?.() ||
        (Date.now().toString(36) + Math.random().toString(36).slice(2, 10)));
      localStorage.setItem('tv_id', id);
    }
    return id;
  } catch (_) {
    return null; // localStorage 차단 환경(시크릿 등) — 방문 수만 집계됨
  }
};

// alltime01 네온 라인 아이콘 세트. 혜택 icon에 이름(gift/shield 등) 지정 시 SVG, 아니면 이모지/글자 폴백.
const BENEFIT_ICONS = {
  gift: '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9"/><path d="M12 8v13"/><path d="M12 8S11 3 7.5 3a2.5 2.5 0 0 0 0 5H12z"/><path d="M12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5H12z"/>',
  bolt: '<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>',
  shield: '<path d="M12 3 5 6v6c0 4.5 3.5 7.5 7 9 3.5-1.5 7-4.5 7-9V6l-7-3z"/><path d="M9 12l2 2 4-4"/>',
  star: '<path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.2l1-5.8L3.5 9.3l5.9-.9L12 3z"/>',
  trophy: '<path d="M8 4h8v5a4 4 0 0 1-8 0V4z"/><path d="M8 5H5v2a3 3 0 0 0 3 3"/><path d="M16 5h3v2a3 3 0 0 1-3 3"/><path d="M12 13v4"/><path d="M9 21h6"/>',
  ticket: '<path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H6a2 2 0 0 1-2-2 2 2 0 0 0 0-4z"/><path d="M14 6v12"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  coins: '<ellipse cx="9" cy="7" rx="6" ry="3"/><path d="M3 7v5c0 1.7 2.7 3 6 3"/><path d="M3 12v5c0 1.7 2.7 3 6 3"/><circle cx="16" cy="15" r="5"/>',
  percent: '<path d="M19 5 5 19"/><circle cx="7.5" cy="7.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/>',
  headset: '<path d="M4 14v-3a8 8 0 0 1 16 0v3"/><path d="M4 14a2 2 0 0 0 2 2h1v-5H6a2 2 0 0 0-2 2z"/><path d="M20 14a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2z"/><path d="M18 16v1a3 3 0 0 1-3 3h-3"/>',
};

// 스크롤 진입 시 페이드+슬라이드업 (alltime01의 .reveal 효과 이식)
const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setShown(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(18px)',
        transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Box>
  );
};

const LandingPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState({ name: '', username: 'testuser0abot', slug: slug || 'main', landing: DEFAULT_LANDING });
  const [currentHero, setCurrentHero] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [rot, setRot] = useState(0);
  const spotRef = useRef(null);

  // 이벤트 로깅 (실패해도 무시)
  const post = (s, event_type, label) =>
    axios.post('/api/tg/events', { slug: s || 'main', event_type, label: label || null, visitor_id: getVisitorId() }).catch(() => {});

  useEffect(() => {
    // slug 경로면 해당 봇, 아니면 접속 도메인(호스트명)으로 봇 자동 매핑
    const req = slug
      ? axios.get(`/api/tg/bots/landing/${slug}`)
      : axios.get('/api/tg/bots/landing/by-host', { params: { host: window.location.hostname } });
    req
      .then((res) => {
        const d = res.data || {};
        const resolved = {
          name: d.name || '',
          username: (d.username || '').replace(/^@/, '') || 'testuser0abot',
          slug: d.slug || slug || 'main',
          landing: { ...DEFAULT_LANDING, ...(d.landing || {}) },
        };
        setData(resolved);
        post(resolved.slug, 'visit', null); // 방문 기록 (해석된 봇 기준)
        // 첫 배경 이미지를 미리 받은 뒤 화면 공개 (수선화 깜빡임 + 배경 늦뜸 동시 방지)
        const imgs = Array.isArray(resolved.landing.hero_images) ? resolved.landing.hero_images : [];
        if (imgs[0]) {
          const im = new Image();
          im.onload = () => setLoaded(true);
          im.onerror = () => setLoaded(true);
          im.src = imgs[0];
          setTimeout(() => setLoaded(true), 1500); // 안전장치: 1.5초 후엔 무조건 공개
        } else {
          setLoaded(true);
        }
      })
      .catch(() => {
        setLoaded(true);
        axios.get('/api/tg/settings/public')
          .then((res) => {
            const name = res.data?.bot_username;
            if (name && /^@?[a-zA-Z0-9_]{5,32}$/.test(name)) {
              setData((p) => ({ ...p, username: name.replace(/^@/, '') }));
            }
          })
          .catch(() => {});
      });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const track = (event_type, label) => post(data.slug, event_type, label);

  const l = data.landing;
  const primary = l.primary || '#c9a96e';
  const goldLight = useMemo(() => shade(primary, 40), [primary]);
  const goldDark = useMemo(() => shade(primary, -40), [primary]);

  // 프로모션 코드: 기간별 스케줄에서 오늘(KST) 코드 자동 선택, 없으면 기본 코드
  const activeCode = useMemo(() => {
    const p = l.promo || {};
    const today = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
    for (const e of (p.schedule || [])) {
      const s = (e.start || '').trim(), en = (e.end || '').trim(), c = (e.code || '').trim();
      if (c && (!s || s <= today) && (!en || today <= en)) return c;
    }
    return (p.code || '').trim();
  }, [l.promo]);

  // 프리미엄 디자인 효과 (봇별 선택: classic / premium / neon)
  const premium = l.fx === 'premium' || l.fx === 'neon';
  const neon = l.fx === 'neon';
  const eventsLarge = l.events_size === 'large'; // EVENTS 카드 크기: 'large'=큰 카드(이미지+텍스트블록), 그 외=작은 오버레이 카드
  // 효과별 개별 on/off (기본=프리미엄 따라감, fxOptions로 끄기 가능)
  const _fxo = l.fxOptions || {};
  const fxScene = premium && _fxo.scene !== false;       // 배경 신스웨이브 씬 + 스캔라인
  const fxSpotlight = premium && _fxo.spotlight !== false; // 마우스 스포트라이트
  const fxFlicker = premium && _fxo.flicker !== false;    // 뱃지 네온 깜빡임
  // 헤드라인 그라데이션 사용 여부(구 설정 폴백용)
  const hlGrad = l.hl_grad !== false;
  const cardGlass = premium ? {
    background: neon ? 'rgba(10,16,32,0.55)' : 'rgba(22,20,26,0.5)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${primary}${neon ? '55' : '33'}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06), 0 20px 50px -20px rgba(0,0,0,.7)',
  } : {};

  // 마우스 따라다니는 스포트라이트 (프리미엄)
  useEffect(() => {
    if (!fxSpotlight) return;
    const onMove = (e) => {
      if (spotRef.current) spotRef.current.style.background =
        `radial-gradient(260px circle at ${e.clientX}px ${e.clientY}px, ${primary}26, transparent 70%)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [fxSpotlight, primary]);

  // 배너/카운트다운 이미지 로테이션용 인덱스 (5초마다)
  useEffect(() => {
    const t = setInterval(() => setRot((r) => r + 1), 5000);
    return () => clearInterval(t);
  }, []);
  const pickRot = (arr, single) => {
    const list = (Array.isArray(arr) && arr.length) ? arr : (single ? [single] : []);
    return list.length ? list[rot % list.length] : '';
  };
  const cdImg = pickRot(l.countdown?.images, l.countdown?.image);
  // 미디어가 동영상(mp4/webm)인지 — 동영상이면 <video>, 아니면 <img>/background
  const isVid = (u) => /\.(mp4|webm)(\?|$)/i.test(u || '');
  // 로고 표시 위치: navbar(상단, 기본) | hero(중앙) | both(양쪽). 분리해 중복 노출 방지.
  const logoPos = l.logo_pos || 'navbar';
  const logoInNav = !!l.logo && (logoPos === 'navbar' || logoPos === 'both');
  const logoInHero = !!l.logo && (logoPos === 'hero' || logoPos === 'both');

  // 배경 이미지: 봇 설정 우선, 없으면 기본 webp
  const useCustomBg = Array.isArray(l.hero_images) && l.hero_images.length > 0;
  const heroes = useCustomBg ? l.hero_images : defaultHeroImages;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHero((prev) => (prev + 1) % heroes.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [heroes.length]);

  const countdownLeft = useCountdown(l.countdown?.enabled ? l.countdown?.deadline : null);
  const tgHref = `https://t.me/${data.username}?start=${data.slug}`;
  const brand = l.title || data.name || '';
  const canonical = (typeof window !== 'undefined') ? (window.location.origin + window.location.pathname) : '';

  // 브랜드 타이틀 글자 스타일 (배너생성기 엔진) — 미설정 시 기존 기본값(명조 + 테마 그라데이션)과 동일
  const brandSx = {
    fontSize: l.brandStyle?.size ? Number(l.brandStyle.size) : { xs: 16, sm: 20, md: 27 },
    fontWeight: 200, letterSpacing: { xs: 4, sm: 6, md: 8 }, lineHeight: 1.2, mb: 1, whiteSpace: 'pre-line',
    ...buildTextSx(l.brandStyle, { primary, defFont: 'serif', defGrad: 'theme' }),
  };
  // 헤드라인(큰 제목) — headStyle 설정 시 풀 엔진, 미설정(미편집)이면 기존 렌더 그대로(완전 동일).
  const headSx = l.headStyle ? {
    fontSize: { xs: 30, sm: 48 }, fontWeight: 900, letterSpacing: '0.02em', lineHeight: 1.25, mt: 1, whiteSpace: 'pre-line',
    filter: `drop-shadow(0 0 18px ${primary}59)`,
    ...buildTextSx(l.headStyle, { primary, defFont: 'orbitron', defGrad: 'theme' }),
  } : {
    fontFamily: "'Orbitron', system-ui, sans-serif", fontSize: { xs: 30, sm: 48 }, fontWeight: 900, letterSpacing: '0.02em',
    lineHeight: 1.25, mt: 1, whiteSpace: 'pre-line',
    ...(hlGrad
      ? { background: `linear-gradient(90deg, ${l.hl_c1 || primary}, ${l.hl_c2 || l.accent2 || goldLight})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', WebkitTextFillColor: 'transparent' }
      : { color: l.hl_c1 || primary }),
    filter: `drop-shadow(0 0 18px ${primary}59)`,
  };
  // 설명 문구(서브헤드라인) — subStyle 설정 시 풀 엔진, 미설정이면 기존 sub_color.
  const subSx = l.subStyle ? {
    fontSize: { xs: 18, md: 22 }, maxWidth: 560, mb: 4, lineHeight: 1.65,
    ...buildTextSx(l.subStyle, { primary, defFont: 'sans', defGrad: 'solid' }),
  } : {
    color: l.sub_color || (premium ? `${goldLight}e0` : 'rgba(255,255,255,0.9)'), fontSize: { xs: 18, md: 22 }, maxWidth: 560, mb: 4, lineHeight: 1.65,
    ...(premium && { textShadow: `0 0 10px ${primary}55` }),
  };
  const dynFontHref = usedFontLink(l);

  // ── SEO/공유 메타 (seo 필드 우선, 비우면 브랜드/히어로 폴백) ──
  const seo = l.seo || {};
  const _abs = (u) => (!u ? '' : (/^https?:\/\//.test(u) ? u : (typeof window !== 'undefined' ? new URL(u, window.location.origin).href : u)));
  const seoTitle = seo.title || `${brand}${l.subtitle ? ' | ' + l.subtitle : ''}`;
  const seoDesc = (seo.description || l.subheadline || l.subtitle || `${brand} - 텔레그램 상담`).slice(0, 200);
  const _ogAuto = (typeof window !== 'undefined') ? `${window.location.origin}/api/tg/bots/og-image/${data.slug}.png` : '';
  const seoImage = _abs(seo.image || (Array.isArray(l.hero_images) && l.hero_images[0]) || l.logo || '') || _ogAuto;
  const canonicalUrl = seo.canonical || canonical;
  // JSON-LD (브라우저·Googlebot용; 크롤러/AI는 Edge가 별도 주입)
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'Organization', name: brand, url: canonicalUrl, description: seoDesc, ...(seoImage ? { logo: seoImage } : {}) },
    { '@context': 'https://schema.org', '@type': 'WebSite', name: brand, url: canonicalUrl },
    { '@context': 'https://schema.org', '@type': 'WebPage', name: seoTitle, description: seoDesc, url: canonicalUrl, inLanguage: 'ko' },
  ];

  const bgUrl = (h) => (useCustomBg ? h : h.s1024 || h.src);

  // 설정/첫 배경 로드 전: 수선화 기본화면 깜빡임 방지용 스플래시
  if (!loaded) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, bgcolor: dark, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <CircularProgress size={36} thickness={4} sx={{ color: primary }} />
      </Box>
    );
  }

  return (
    <Box component="main" sx={{
      bgcolor: dark, color: '#fff', minHeight: '100vh', overflowX: 'hidden',
      fontFamily: "'Pretendard', 'Noto Sans KR', sans-serif", position: 'relative',
    }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        <meta name="robots" content={seo.noindex ? 'noindex, nofollow' : 'index, follow'} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {/* Open Graph (카카오톡·페북·텔레그램 공유 카드) */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="og:site_name" content={brand} />
        <meta property="og:locale" content="ko_KR" />
        {/* Twitter */}
        <meta name="twitter:card" content={seoImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
        {/* 구조화 데이터(JSON-LD) */}
        {jsonLd.map((j, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(j)}</script>
        ))}
        <link href="https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@200;300;400;500&family=Orbitron:wght@500;700;900&display=swap" rel="stylesheet" />
        {dynFontHref && <link href={dynFontHref} rel="stylesheet" />}
      </Helmet>

      {/* 배경 로테이션 */}
      {heroes.map((hero, i) => (
        <Box key={i} sx={{
          position: 'fixed', inset: 0, zIndex: 0,
          backgroundImage: `url(${useCustomBg ? hero : (bgUrl(hero))})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: i === currentHero ? 1 : 0,
          transition: 'opacity 2s ease-in-out',
          filter: `brightness(${Math.max(0.15, 1 - (l.overlay ?? 0.6))}) blur(${l.blur || 0}px) saturate(0.8)`,
        }} />
      ))}

      {/* 오버레이 */}
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: 1,
        background: `linear-gradient(180deg, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.3) 35%, rgba(10,10,10,0.45) 70%, rgba(10,10,10,0.9) 100%)`,
      }} />

      {/* 프리미엄 신스웨이브 배경 씬 (오브·별·바닥격자·수평선 블룸) + 스캔라인 */}
      {premium && <style>{FX_KEYFRAMES}</style>}
      {fxScene && (
        <>
          <BgScene c1={primary} c2={l.accent2 || primary} />
          <Box sx={{ position: 'fixed', inset: 0, zIndex: 2, pointerEvents: 'none', opacity: 0.28, mixBlendMode: 'overlay',
            background: 'repeating-linear-gradient(0deg, rgba(0,0,0,.18) 0 1px, transparent 1px 3px)' }} />
        </>
      )}
      {fxSpotlight && <Box ref={spotRef} sx={{ position: 'fixed', inset: 0, zIndex: 3, pointerEvents: 'none', '@media (hover:none)': { display: 'none' } }} />}

      {/* 상단 라인 */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: '1px', zIndex: 10,
        background: `linear-gradient(90deg, transparent 5%, ${primary}66 50%, transparent 95%)` }} />

      {/* Navbar */}
      <Box component="header" sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, height: 72 }}>
        <Container maxWidth="md" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          {logoInNav ? (
            /\.(mp4|webm)(\?|$)/i.test(l.logo) ? (
              <Box component="video" src={l.logo} autoPlay muted loop playsInline preload="auto"
                sx={{ height: { xs: 40, md: 52 }, maxWidth: 260, objectFit: 'contain' }} />
            ) : (
              <Box component="img" src={l.logo} alt={brand} sx={{ height: { xs: 38, md: 50 }, maxWidth: 240, objectFit: 'contain' }} />
            )
          ) : (
            <Typography sx={{ fontFamily: "'Noto Serif KR', serif", fontWeight: 300, fontSize: { xs: 16, md: 18 }, letterSpacing: 6, color: `${primary}cc` }}>
              {brand}
            </Typography>
          )}
        </Container>
      </Box>

      {/* 메인 컨텐츠 */}
      <Box sx={{
        position: 'relative', zIndex: 5, minHeight: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        px: 3, py: 12, textAlign: 'center',
      }}>
        {l.badge && (
          <Chip label={l.badge} className={fxFlicker ? 'fxFlick' : undefined} sx={{
            mb: 3, bgcolor: 'rgba(255,255,255,0.08)', color: primary,
            border: `1px solid ${primary}${premium ? 'aa' : '55'}`, letterSpacing: 1,
            ...(premium && { boxShadow: `0 0 14px ${primary}66, inset 0 0 8px ${primary}2e`, textShadow: `0 0 8px ${primary}aa` }),
          }} />
        )}

        <Box sx={{ width: 8, height: 8, mb: 4, border: `1px solid ${primary}4d`, transform: 'rotate(45deg)' }} />

        {logoInHero ? (
          /\.(mp4|webm)(\?|$)/i.test(l.logo) ? (
            <Box component="video" src={l.logo} autoPlay muted loop playsInline preload="auto" sx={{ maxHeight: 100, maxWidth: 300, mb: 1, objectFit: 'contain' }} />
          ) : (
            <Box component="img" src={l.logo} alt={brand} sx={{ maxHeight: 90, maxWidth: 280, mb: 1, objectFit: 'contain' }} />
          )
        ) : (
          <Typography sx={brandSx}>
            {brand}
          </Typography>
        )}

        {l.headline && (
          <Typography sx={headSx}>
            {l.headline}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 4 }}>
          <Box sx={{ width: { xs: 40, md: 60 }, height: '1px', background: `linear-gradient(90deg, transparent, ${primary}66)` }} />
          <Box sx={{ width: 5, height: 5, border: `1px solid ${primary}4d`, transform: 'rotate(45deg)' }} />
          <Box sx={{ width: { xs: 40, md: 60 }, height: '1px', background: `linear-gradient(90deg, ${primary}66, transparent)` }} />
        </Box>

        <Typography sx={{
          color: premium ? goldLight : 'rgba(255,255,255,0.88)', fontSize: { xs: 20, md: 24 },
          letterSpacing: { xs: 1, md: 2 }, fontWeight: 600, mb: l.subheadline ? 2 : 5,
          ...(premium && { textShadow: `0 0 16px ${primary}99` }),
        }}>
          {l.subtitle}
        </Typography>

        {l.subheadline && (
          <Typography sx={subSx}>
            {l.subheadline}
          </Typography>
        )}

        {/* 신뢰 요소 (소셜 프루프) — BENEFITS형 네온 카드 + 스크롤 리빌 */}
        {l.trust_stats?.length > 0 && (
          <Box sx={{ width: '100%', maxWidth: 760, mb: 4 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: l.trust_stats.length >= 3 ? 'repeat(2, 1fr)' : `repeat(${l.trust_stats.length}, 1fr)`,
                sm: `repeat(${Math.min(4, l.trust_stats.length)}, 1fr)`,
              },
              gap: { xs: 1.5, md: 2 },
            }}>
              {l.trust_stats.slice(0, 4).map((s, i) => (
                <Reveal key={i} delay={i * 90}>
                  <Box sx={{
                    textAlign: 'center', p: { xs: 1.8, md: 2.4 }, borderRadius: 2, height: '100%',
                    transition: 'transform .3s, box-shadow .3s, border-color .3s',
                    ...(premium ? cardGlass : { border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }),
                    '&:hover': { transform: 'translateY(-5px)', ...(premium && { borderColor: primary, boxShadow: `0 0 26px ${primary}3a, inset 0 1px 0 rgba(255,255,255,.06)` }) },
                  }}>
                    {s.icon && (
                      <Box sx={{
                        width: 46, height: 46, mx: 'auto', mb: 1, borderRadius: '50%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 22,
                        border: `1.5px solid ${primary}88`, color: primary, background: `${primary}12`,
                        ...(premium && { boxShadow: `0 0 16px ${primary}44, inset 0 0 12px ${primary}22` }),
                      }}>{s.icon}</Box>
                    )}
                    <Typography sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 800, color: primary, lineHeight: 1.1, ...(premium && { textShadow: `0 0 14px ${primary}66` }) }}>
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 10, md: 11 }, color: 'rgba(255,255,255,0.55)', letterSpacing: 1, mt: 0.5 }}>
                      {s.label}
                    </Typography>
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Box>
        )}

        {/* 프로모션 코드 (link 있으면 클릭 가능) */}
        {l.promo?.enabled && activeCode && (
          <Box component={l.promo.link ? 'a' : 'div'} href={l.promo.link || undefined}
            target="_blank" rel="noopener noreferrer"
            onClick={l.promo.link ? () => track('cta_click', 'promo:' + activeCode) : undefined}
            sx={{
              display: 'block', textDecoration: 'none', color: 'inherit',
              mb: 4, px: 3, py: 1.5, border: `1px dashed ${primary}`, borderRadius: 1,
              ...(l.promo.link && { cursor: 'pointer', transition: 'all .2s', '&:hover': { borderStyle: 'solid', boxShadow: `0 0 16px ${primary}44` } }),
            }}>
            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: 2 }}>{l.promo.label}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: 2, color: primary }}>{activeCode}</Typography>
            {l.promo.note && <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{l.promo.note}{l.promo.link ? ' →' : ''}</Typography>}
          </Box>
        )}

        {/* CTA */}
        <Button component="a" href={tgHref} target="_blank" rel="noopener noreferrer"
          onClick={() => track('cta_click', l.button_text)}
          sx={premium ? {
            position: 'relative', overflow: 'hidden',
            background: `linear-gradient(90deg, ${primary}, ${l.accent2 || goldDark})`, color: '#0a0a0a',
            border: 'none', borderRadius: 999, px: { xs: 5, sm: 6, md: 8 }, py: { xs: 1.6, md: 2 },
            fontSize: { xs: 13, md: 15 }, fontWeight: 800, letterSpacing: { xs: 1, md: 2 }, transition: 'all 0.3s',
            boxShadow: `0 0 24px ${primary}66, 0 10px 30px -8px ${primary}55`,
            '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 0 40px ${primary}99, 0 14px 38px -8px ${primary}88` },
            '&::after': { content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'linear-gradient(115deg, transparent 32%, rgba(255,255,255,.5) 50%, transparent 68%)', transform: 'translateX(-130%)' },
            '&:hover::after': { animation: 'fxSweep .75s ease' },
          } : {
            background: 'transparent', color: primary, border: `1px solid ${primary}33`, borderRadius: 0,
            px: { xs: 5, sm: 6, md: 8 }, py: { xs: 1.8, md: 2.2 }, fontSize: { xs: 12, md: 13 },
            fontWeight: 400, letterSpacing: { xs: 4, md: 6 }, transition: 'all 0.4s',
            '&:hover': { borderColor: `${primary}88`, color: goldLight, boxShadow: `0 0 50px ${primary}22` },
          }}>
          {l.button_text || '텔레그램 상담'}
        </Button>

        {/* 카운트다운 (이미지/동영상 배경 + 링크 옵션) */}
        {l.countdown?.enabled && countdownLeft && (
          <Box component={l.countdown.link ? 'a' : 'div'} href={l.countdown.link || undefined}
            target="_blank" rel="noopener noreferrer"
            onClick={l.countdown.link ? () => track('banner_click', 'countdown') : undefined}
            sx={{
              position: 'relative', display: 'block', textDecoration: 'none', color: 'inherit',
              mt: 4, width: '100%', maxWidth: 560, borderRadius: 2, overflow: 'hidden',
              px: { xs: 3, md: 5 }, py: { xs: 2.5, md: 3 },
              ...(l.countdown.link && { cursor: 'pointer', transition: 'transform .2s, box-shadow .2s', '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 10px 30px rgba(0,0,0,.4)` } }),
            ...(cdImg && !isVid(cdImg) ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,.12), rgba(0,0,0,.36)), url(${cdImg})`,
              backgroundSize: 'cover', backgroundPosition: 'center',
              filter: 'saturate(1.2) contrast(1.05) brightness(1.2)',
            } : {}),
            border: cdImg ? 'none' : `1px solid ${primary}33`,
          }}>
            {cdImg && isVid(cdImg) && (
              <>
                <Box component="video" src={cdImg} autoPlay muted loop playsInline preload="auto"
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(0,0,0,.12), rgba(0,0,0,.36))', zIndex: 0 }} />
              </>
            )}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: 'rgba(255,255,255,0.9)', letterSpacing: 2, textShadow: '0 1px 6px rgba(0,0,0,.8)' }}>
                {l.countdown.title}
              </Typography>
              <Typography sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: l.countdown?.color || l.accent2 || primary, mt: 0.5, textShadow: '0 2px 10px rgba(0,0,0,.85)' }}>
                ⏳ {countdownLeft}
              </Typography>
            </Box>
          </Box>
        )}

        {/* 카운트다운 하단 이미지/동영상 (약간 작게, link 있으면 클릭 가능) */}
        {l.countdown?.enabled && l.countdown.bottom_image && (
          <Box component={l.countdown.link ? 'a' : 'div'} href={l.countdown.link || undefined}
            target="_blank" rel="noopener noreferrer"
            onClick={l.countdown.link ? () => track('banner_click', 'countdown-bottom') : undefined}
            sx={{ display: 'block', mt: 1.5, width: '100%', maxWidth: 500, mx: 'auto', ...(l.countdown.link && { cursor: 'pointer' }) }}>
            <Box
              component={isVid(l.countdown.bottom_image) ? 'video' : 'img'}
              src={l.countdown.bottom_image}
              alt="event"
              {...(isVid(l.countdown.bottom_image) ? { autoPlay: true, muted: true, loop: true, playsInline: true, preload: 'auto' } : {})}
              sx={{ width: '100%', borderRadius: 2, display: 'block', objectFit: 'cover' }}
            />
          </Box>
        )}

        {/* EVENTS (배너) — events_size로 작은 오버레이 카드 / 큰 카드 선택 */}
        {l.banners?.length > 0 && (
          <Box sx={{ mt: 5, maxWidth: eventsLarge ? 1040 : 900, width: '100%' }}>
            <Typography sx={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: { xs: 17, md: 22 }, letterSpacing: 5, color: primary, textAlign: 'center', mb: 0.6, ...(premium && { textShadow: `0 0 12px ${primary}88` }) }}>EVENTS</Typography>
            <Box sx={{ width: 64, height: 2, bgcolor: primary, mx: 'auto', mb: 3, ...(premium && { boxShadow: `0 0 8px ${primary}` }) }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, md: eventsLarge ? 3 : 2.5 } }}>
            {l.banners.slice(0, 4).map((b, i) => {
              const img = pickRot(b.images, b.image);
              return eventsLarge ? (
                /* 큰 카드: 상단 16:10 이미지 + 하단 텍스트 블록 (alltime01 EVENTS형) */
                <Reveal key={i} delay={i * 80}>
                  <Box component={b.link ? 'a' : 'div'} href={b.link || undefined} target="_blank" rel="noopener noreferrer"
                    onClick={() => track('banner_click', b.title)}
                    sx={{
                      display: 'block', textDecoration: 'none', color: '#fff', borderRadius: 3, overflow: 'hidden', height: '100%',
                      background: 'rgba(255,255,255,0.02)',
                      border: premium ? `1px solid ${primary}40` : '1px solid rgba(255,255,255,0.12)',
                      transition: 'transform .3s, box-shadow .3s, border-color .3s',
                      ...(premium && { boxShadow: `0 8px 24px -10px ${primary}33` }),
                      '&:hover': { transform: 'translateY(-6px)', borderColor: premium ? primary : `${primary}88`,
                        boxShadow: premium ? `0 0 34px ${primary}44, 0 18px 40px rgba(0,0,0,.5)` : `0 12px 32px rgba(0,0,0,.45)` },
                      '&:hover img, &:hover video': { transform: 'scale(1.06)' },
                    }}>
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.3)' }}>
                      {img && <Box component={isVid(img) ? 'video' : 'img'} src={img} alt={b.title || ''}
                        {...(isVid(img) ? { autoPlay: true, muted: true, loop: true, playsInline: true, preload: 'auto' } : {})}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .5s ease', filter: 'saturate(1.15) contrast(1.05)' }} />}
                    </Box>
                    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Typography sx={{ fontSize: { xs: 17, md: 20 }, fontWeight: 800, color: '#fff' }}>{b.title}</Typography>
                      {b.text && <Typography sx={{ fontSize: { xs: 13, md: 14 }, color: 'rgba(255,255,255,0.7)', mt: 0.6, lineHeight: 1.6 }}>{b.text}</Typography>}
                      {b.link && <Typography sx={{ mt: 1.4, fontSize: { xs: 12, md: 13 }, fontWeight: 800, letterSpacing: 1, color: primary, ...(premium && { textShadow: `0 0 10px ${primary}66` }) }}>바로가기 →</Typography>}
                    </Box>
                  </Box>
                </Reveal>
              ) : (
                /* 작은 카드: 이미지 위 텍스트 오버레이 (기존) */
                <Reveal key={i} delay={i * 80}>
                  <Box component={b.link ? 'a' : 'div'} href={b.link || undefined} target="_blank" rel="noopener noreferrer"
                    onClick={() => track('banner_click', b.title)}
                    sx={{
                      position: 'relative',
                      textDecoration: 'none', color: '#fff', borderRadius: 2, overflow: 'hidden', height: '100%',
                      border: premium ? `1px solid ${primary}40` : '1px solid rgba(255,255,255,0.12)', minHeight: { xs: 150, md: 210 },
                      ...(img && !isVid(img) ? {
                        backgroundImage: `linear-gradient(rgba(0,0,0,0) 40%, rgba(0,0,0,.6) 100%), url(${img})`,
                        backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(1.18) contrast(1.06) brightness(1.06)',
                      } : { background: img ? '#000' : 'rgba(255,255,255,0.04)' }),
                      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: { xs: 2, md: 2.5 },
                      transition: 'transform .3s, box-shadow .3s, border-color .3s',
                      ...(premium && { boxShadow: `0 8px 24px -10px ${primary}33` }),
                      '&:hover': { transform: 'translateY(-6px)', borderColor: premium ? primary : `${primary}88`,
                        boxShadow: premium ? `0 0 30px ${primary}44, 0 16px 38px rgba(0,0,0,.5)` : `0 10px 30px rgba(0,0,0,.4)` },
                    }}>
                    {img && isVid(img) && (
                      <>
                        <Box component="video" src={img} autoPlay muted loop playsInline preload="auto"
                          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
                        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(0,0,0,0) 40%, rgba(0,0,0,.6) 100%)', zIndex: 0 }} />
                      </>
                    )}
                    <Typography sx={{ position: 'relative', zIndex: 1, fontSize: { xs: 16, md: 19 }, fontWeight: 800, textShadow: '0 2px 8px rgba(0,0,0,.9)' }}>{b.title}</Typography>
                    {b.text && <Typography sx={{ position: 'relative', zIndex: 1, fontSize: { xs: 12, md: 13 }, color: 'rgba(255,255,255,0.92)', mt: 0.5, textShadow: '0 1px 6px rgba(0,0,0,.9)' }}>{b.text}</Typography>}
                  </Box>
                </Reveal>
              );
            })}
            </Box>
          </Box>
        )}

        {/* BENEFITS (혜택 카드 섹션) */}
        {l.benefits?.length > 0 && (
          <Box sx={{ mt: 6, maxWidth: 980, width: '100%' }}>
            <Typography sx={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: { xs: 17, md: 22 }, letterSpacing: 5, color: primary, textAlign: 'center', mb: 0.6, ...(premium && { textShadow: `0 0 12px ${primary}88` }) }}>BENEFITS</Typography>
            <Box sx={{ width: 64, height: 2, bgcolor: primary, mx: 'auto', mb: 3.5, ...(premium && { boxShadow: `0 0 8px ${primary}` }) }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 2.5 } }}>
              {l.benefits.slice(0, 6).map((bf, i) => (
                <Reveal key={i} delay={i * 90}>
                  <Box sx={{ textAlign: 'center', p: { xs: 3, md: 3.5 }, borderRadius: 3, height: '100%',
                    transition: 'transform .3s, box-shadow .3s, border-color .3s',
                    ...(premium ? cardGlass : { background: 'rgba(255,255,255,0.02)', border: `1px solid ${primary}2e` }),
                    '&:hover': {
                      transform: 'translateY(-4px)', borderColor: primary,
                      boxShadow: `0 0 26px ${primary}3a${premium ? ', inset 0 1px 0 rgba(255,255,255,.06)' : ''}`,
                    },
                    '&:hover .b-ico': { transform: 'translateY(-2px) scale(1.04)', boxShadow: `0 0 28px ${primary}73, inset 0 0 18px ${primary}30` },
                  }}>
                    <Box className="b-ico" sx={{
                      width: 66, height: 66, mx: 'auto', mb: 2, borderRadius: '50%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 28, color: primary,
                      border: `1.5px solid ${primary}73`, background: `${primary}0f`,
                      boxShadow: `0 0 18px ${primary}38, inset 0 0 14px ${primary}1f`,
                      transition: 'transform .3s, box-shadow .3s',
                    }}>
                      {BENEFIT_ICONS[bf.icon] ? (
                        <Box component="svg" viewBox="0 0 24 24" aria-hidden="true"
                          sx={{ width: 30, height: 30, fill: 'none', stroke: primary, strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round', filter: `drop-shadow(0 0 5px ${primary}cc)` }}
                          dangerouslySetInnerHTML={{ __html: BENEFIT_ICONS[bf.icon] }} />
                      ) : (bf.icon || '✦')}
                    </Box>
                    <Typography sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 700, color: '#fff', mb: 0.8 }}>{bf.title}</Typography>
                    <Typography sx={{ fontSize: { xs: 13.5, md: 14.5 }, color: 'rgba(255,255,255,0.66)', lineHeight: 1.65 }}>{bf.text}</Typography>
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Box>
        )}

        <Typography sx={{ color: 'rgba(255,255,255,0.12)', fontSize: 10, mt: 5, letterSpacing: 3 }}>
          {l.available_text || '24H AVAILABLE'}
        </Typography>
      </Box>

      {/* 푸터 */}
      <Box sx={{ position: 'relative', zIndex: 5, textAlign: 'center', pb: 2 }}>
        {l.footer_note && (
          <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 9, mb: 0.5 }}>{l.footer_note}</Typography>
        )}
        <Typography sx={{ color: 'rgba(255,255,255,0.15)', fontSize: 9, letterSpacing: 2 }}>
          &copy; {new Date().getFullYear()} {l.footer || brand}
        </Typography>
      </Box>

      {/* 플로팅 상담 버튼 (항상 표시 — 이탈 직전 전환) */}
      {l.floating_cta && (
        <Button
          component="a" href={tgHref} target="_blank" rel="noopener noreferrer"
          onClick={() => track('floating_click', l.button_text)}
          sx={{
            position: 'fixed', right: { xs: 16, md: 28 }, bottom: { xs: 16, md: 28 }, zIndex: 1000,
            bgcolor: primary, color: '#0a0a0a', fontWeight: 800, borderRadius: 8,
            px: { xs: 2.5, md: 3.5 }, py: { xs: 1.2, md: 1.5 }, fontSize: { xs: 13, md: 15 },
            boxShadow: `0 8px 24px ${primary}66`,
            '&:hover': { bgcolor: goldLight, transform: 'translateY(-2px)' },
            transition: 'transform .2s',
          }}
        >
          💬 {l.button_text || '텔레그램 상담'}
        </Button>
      )}
    </Box>
  );
};

export default LandingPage;
